import React, { useEffect, useState } from 'react';
import { Plus, MapPin, Trash2, X, Loader2, RefreshCw, Building2 } from 'lucide-react';
import { teamsApi, TeamRecord, TeamRegionRow } from '../services/creatorApi';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { toast } from 'sonner';
export function Regions() {
  const {
    currentWorkspace,
    refreshTeams
  } = useWorkspace();
  const [team, setTeam] = useState<TeamRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [newRegion, setNewRegion] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const fetchTeam = async () => {
    setLoading(true);
    try {
      const teamData = await teamsApi.findByTeamName(currentWorkspace.name);
      setTeam(teamData);
    } catch (err) {
      console.error('Failed to fetch team:', err);
      toast.error('Failed to load team configuration');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchTeam();
  }, [currentWorkspace.name]);
  const handleCreateTeam = async () => {
    setSaving(true);
    try {
      const teamId = teamsApi.generateTeamId(currentWorkspace.name);
      await teamsApi.create({
        Team_Name: currentWorkspace.name,
        Team_ID: teamId,
        Team_Regions: []
      });
      toast.success('Team configuration created');
      fetchTeam();
    } catch (err) {
      console.error('Failed to create team:', err);
      toast.error('Failed to create team configuration');
    } finally {
      setSaving(false);
    }
  };
  const handleAddRegion = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newRegion.trim() || !team) return;
    // Check if region already exists
    const existingRegions = team.Team_Regions || [];
    if (existingRegions.some(r => r.Region_Name === newRegion.trim())) {
      toast.error('Region already exists');
      return;
    }
    setSaving(true);
    try {
      const regionId = newRegion.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
      const updatedRegions: TeamRegionRow[] = [...existingRegions, {
        Region_Name: newRegion.trim()
      }];
      await teamsApi.update(team.ID, {
        Team_Regions: updatedRegions
      });
      toast.success('Region added successfully');
      setNewRegion('');
      setShowModal(false);
      fetchTeam();
      refreshTeams();
    } catch (err) {
      console.error('Failed to add region:', err);
      toast.error('Failed to add region');
    } finally {
      setSaving(false);
    }
  };
  const handleDeleteRegion = async (regionName: string) => {
    if (!team) return;
    setSaving(true);
    try {
      const updatedRegions = (team.Team_Regions || []).filter(r => r.Region_Name !== regionName);
      await teamsApi.update(team.ID, {
        Team_Regions: updatedRegions
      });
      toast.success('Region removed');
      setDeleteConfirm(null);
      fetchTeam();
      refreshTeams();
    } catch (err) {
      console.error('Failed to delete region:', err);
      toast.error('Failed to remove region');
    } finally {
      setSaving(false);
    }
  };
  const regions = team?.Team_Regions || [];
  if (loading) {
    return <div className="flex items-center justify-center min-h-[400px]" data-id="element-5699" data-mp="3ett9a0hrssgt">
        <Loader2 className="h-8 w-8 text-blue-500 animate-spin" data-id="element-5700" data-mp="gb4ejkddhag1" />
      </div>;
  }
  // If no team configuration exists, show setup prompt
  if (!team) {
    return <div className="space-y-6" data-id="element-5701" data-mp="6com9e2lzv4y">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-5702" data-mp="6bwyuqj613pr">
          <div className="min-w-0" data-id="element-5703" data-mp="2buwef5lw69f">
            <h1 className="text-2xl font-semibold text-gray-900" data-id="element-5704" data-mp="2atmwf48nrtt">Regions</h1>
            <p className="mt-1 text-sm text-gray-500" data-id="element-5705" data-mp="2r3p2xld76em4">
              Manage regions for {currentWorkspace.name}.
            </p>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-12 text-center" data-id="element-5706" data-mp="esm704p6bzpl">
          <Building2 className="mx-auto h-12 w-12 text-gray-400" data-id="element-5707" data-mp="eqszyoanfdh2" />
          <h3 className="mt-4 text-lg font-medium text-gray-900" data-id="element-5708" data-mp="jkx7b8nttnio">
            Team Not Set Up Yet
          </h3>
          <p className="mt-2 text-sm text-gray-500 max-w-md mx-auto" data-id="element-5709" data-mp="4w9e391gdpvw">
            Before adding regions, you need to initialize the team
            configuration. This will set up your team's regions and settings.
          </p>
          <button onClick={handleCreateTeam} disabled={saving} className="mt-6 inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50" data-id="element-5710" data-mp="12i6wl6fu1v7u">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" data-id="element-5711" data-mp="slvchua0412u" /> : <Plus className="h-4 w-4" data-id="element-5712" data-mp="397d30b85s8xu" />}
            Initialize Team
          </button>
        </div>
      </div>;
  }
  return <div className="space-y-6" data-id="element-5713" data-mp="35q7hy77f79bh">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-5714" data-mp="35pfjmomfuxhi">
        <div className="min-w-0" data-id="element-5715" data-mp="30qm8v3yjdkl1">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-5716" data-mp="30tqjaj2lhck5">Regions</h1>
          <p className="mt-1 text-sm text-gray-500" data-id="element-5717" data-mp="3fnlj6zkrvrj7">
            Manage regions for {currentWorkspace.name}. Regions are used to
            scope and filter data across modules.
          </p>
        </div>
        <div className="flex items-center gap-3" data-id="element-5718" data-mp="2n0r1gafcb69f">
          <button onClick={fetchTeam} disabled={loading} className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md" title="Refresh" data-id="element-5719" data-mp="2i1eezqsds17d">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} data-id="element-5720" data-mp="2rz2cupla9otr" />
          </button>
          <button onClick={() => setShowModal(true)} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 whitespace-nowrap flex-shrink-0" data-id="element-5721" data-mp="248yasmnqtx4v">
            <Plus className="h-4 w-4 flex-shrink-0" data-id="element-5722" data-mp="2oyk55lz445rc" />
            <span data-id="element-5723" data-mp="2szm2fx9gy7qo">Add Region</span>
          </button>
        </div>
      </div>

      {/* Team Info Card */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-5724" data-mp="2xs5gevc2j4w0">
        <div className="flex items-center gap-3" data-id="element-5725" data-mp="32pxk0mm2sfg1">
          <Building2 className="h-5 w-5 text-blue-600" data-id="element-5726" data-mp="32pp38jmu0pzk" />
          <div data-id="element-5727" data-mp="260g3tdpgvb03">
            <div className="text-sm font-medium text-blue-900" data-id="element-5728" data-mp="260znozyog9gr">
              {team.Team_Name}
            </div>
            <div className="text-xs text-blue-700 font-mono" data-id="element-5729" data-mp="2a2b90taiq8j5">
              ID: {team.Team_ID}
            </div>
          </div>
          <span className={`ml-auto px-2 py-0.5 rounded text-xs font-medium ${team.Status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`} data-id="element-5730" data-mp="2f2pcdy16cuau">
            {team.Status}
          </span>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-5731" data-mp="3vdtavaoyrd4w">
        {regions.length === 0 ? <div className="p-12 text-center" data-id="element-5732" data-mp="1jqr9m6sgi6de">
            <MapPin className="mx-auto h-12 w-12 text-gray-400" data-id="element-5733" data-mp="1espt9tmf0l5m" />
            <h3 className="mt-2 text-sm font-semibold text-gray-900" data-id="element-5734" data-mp="1euiyd4hdqxbi">
              No regions defined
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-5735" data-mp="152mnesu73cpv">
              Add regions to segment data by geographic or logical areas.
            </p>
            <button onClick={() => setShowModal(true)} className="mt-4 inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700" data-id="element-5736" data-mp="111t123wei5z3">
              <Plus className="h-4 w-4" data-id="element-5737" data-mp="r3wrhgj43j33" />
              Add your first region
            </button>
          </div> : <div className="overflow-x-auto" data-id="element-5738" data-mp="m5bt7mnf79ha">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-5739" data-mp="2lgmk7uxqum8w">
              <thead className="bg-gray-50" data-id="element-5740" data-mp="2lesa7bo0gejl">
                <tr data-id="element-5741" data-mp="2ghjr98v1g6ha">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5742" data-mp="2gj374ycs8fij">
                    Region Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5743" data-mp="1uc22igzvees3">
                    Region ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5744" data-mp="1gib4o5vi4jc7">
                    Team
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5745" data-mp="16l6hq3tu0juc">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-5746" data-mp="xmugl7vca3nn">
                {regions.map(region => <tr key={region.Region_ID} className="hover:bg-gray-50" data-id="element-5747" data-mp="11n4v58x762e4">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-5748" data-mp="2s0lxmsfzzpt4">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-blue-50 text-blue-700 border border-blue-200" data-id="element-5749" data-mp="2w1eadvo2wcu5">
                        <MapPin className="h-3.5 w-3.5 flex-shrink-0" data-id="element-5750" data-mp="kbg30g5klb34" />
                        {region.Region_Name}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-5751" data-mp="u43zf9sn1d1b">
                      <code className="text-xs font-mono text-gray-500 bg-gray-100 px-2 py-1 rounded" data-id="element-5752" data-mp="pc3ut41mhvye">
                        {region.Region_ID}
                      </code>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-5753" data-mp="z2yjef2cd7n0">
                      {team.Team_Name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-5754" data-mp="1d1exir2ja0f6">
                      <button onClick={() => setDeleteConfirm(region.Region_Name)} className="text-red-600 hover:text-red-900 p-2 hover:bg-red-50 rounded" title="Delete" data-id="element-5755" data-mp="183wmmv4yhb4j">
                        <Trash2 className="h-4 w-4" data-id="element-5756" data-mp="2pecj9sync5ux" />
                      </button>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>}
      </div>

      {/* Create Modal */}
      {showModal && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-5757" data-mp="2be1ssr190taa">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-5758" data-mp="2bflb0n6gteep">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowModal(false)} data-id="element-5759" data-mp="3839ga4lfhcgu" />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md p-6" data-id="element-5760" data-mp="2zb7ike2quaa9">
              <div className="flex items-center justify-between mb-4" data-id="element-5761" data-mp="2uce3wibq0d8i">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-5762" data-mp="2uc4cqrexw018">
                  Add New Region
                </h3>
                <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-500" data-id="element-5763" data-mp="3rtl9rutkqy0d">
                  <X className="h-5 w-5" data-id="element-5764" data-mp="3udhrmq09l1gi" />
                </button>
              </div>
              <form onSubmit={handleAddRegion} className="space-y-4" data-id="element-5765" data-mp="36y9sqw0gb8p">
                <div data-id="element-5766" data-mp="8589nesok00n">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5767" data-mp="83x2d5010a4s">
                    Region Name
                  </label>
                  <div className="relative" data-id="element-5768" data-mp="c4h12d08zlt1">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" data-id="element-5769" data-mp="h6g41ls2sgpg" />
                    <input type="text" required className="block w-full rounded-md border-gray-300 pl-10 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={newRegion} onChange={e => setNewRegion(e.target.value)} placeholder="e.g., Sao Paulo, North, Retail Team" data-id="element-5770" data-mp="m3zh4id1enft" />
                  </div>
                </div>
                <div data-id="element-5771" data-mp="30xvpbkmgrwe9">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5772" data-mp="30zf4t90aq3ez">
                    Team
                  </label>
                  <input type="text" className="block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm sm:text-sm text-gray-500 cursor-not-allowed" value={team.Team_Name} disabled data-id="element-5773" data-mp="2wymsu801hkf0" />
                </div>
                <div className="flex justify-end gap-3 pt-4" data-id="element-5774" data-mp="3koh6usw3hf0u">
                  <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-5775" data-mp="3fmi2pgsj6e2u">
                    Cancel
                  </button>
                  <button type="submit" disabled={!newRegion.trim() || saving} className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2" data-id="element-5776" data-mp="1z99zjjz0qs7g">
                    {saving && <Loader2 className="h-4 w-4 animate-spin" data-id="element-5777" data-mp="et3oymmdapcd" />}
                    Add Region
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>}

      {/* Delete Confirmation */}
      {deleteConfirm && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-5778" data-mp="3n57whzyv00h1">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-5779" data-mp="3s65muzu0w0tm">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setDeleteConfirm(null)} data-id="element-5780" data-mp="3e6e1ge16fi5l" />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-sm p-6" data-id="element-5781" data-mp="3i87zeejzm822">
              <div className="flex items-center gap-4 mb-4" data-id="element-5782" data-mp="1cg2n71spk5cd">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center" data-id="element-5783" data-mp="1gg2yidkig0f7">
                  <Trash2 className="h-5 w-5 text-red-600" data-id="element-5784" data-mp="rpyoeikiwc6w" />
                </div>
                <div data-id="element-5785" data-mp="mqvjqz8gg7zg">
                  <h3 className="text-lg font-medium text-gray-900" data-id="element-5786" data-mp="mqc8g1db8hue">
                    Remove Region
                  </h3>
                  <p className="text-sm text-gray-500" data-id="element-5787" data-mp="hpy2qpz6ljzj">
                    Remove "{deleteConfirm}" from {team.Team_Name}? This won't
                    delete CRM records tagged with this region.
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-3" data-id="element-5788" data-mp="3zkxivdocjdw">
                <button onClick={() => setDeleteConfirm(null)} disabled={saving} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-5789" data-mp="3y1faz8gjy9h">
                  Cancel
                </button>
                <button onClick={() => handleDeleteRegion(deleteConfirm)} disabled={saving} className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center gap-2" data-id="element-5790" data-mp="3h6mjxwa4my3d">
                  {saving && <Loader2 className="h-4 w-4 animate-spin" data-id="element-5791" data-mp="25pjr9zkwp4l7" />}
                  Remove
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}